// docs/112 UP7 — a workshop's counter: what its keeper can lay on a piece (the upgrades on offer), and
// for the chosen one, every piece of the chosen hero's that it fits (worn or packed) with the rank it
// would reach and what that costs, what it adds, and the hero's numbers after; then pay. The rules'
// own `upgrade_options` (wasm) say what fits and what it costs; `upgrade_item` (the module) judges.
import { useMemo, useState, type CSSProperties } from 'react';
import { Badge, Button, OryxIcon, Panel, Segmented } from '../../components';
import { getBridge } from '../../../core/encounter/session';
import { upgradeSpec, type UpgradeSpec } from '../../../core/items/catalogue';
import { describeUpgrade, fitsLine, ranksOf, roman, shopKind, SHOP_TITLES, SHOP_TRADES, upgradedName, type ShopKey } from '../../../core/items/upgrades';
import { statDiff, STAT_ROWS, statValue, upgradeSegs } from '../../../core/sim/gear';
import { ItemIcon } from '../../inventory/ItemCard';
import { useHeroGear, type GearItem } from '../../inventory/useHeroGear';

export interface WorkshopView { id: bigint; shop: ShopKey; keeper: string; offers: number[]; frame?: number }
export interface WorkshopHero { id: bigint; name: string; gold: number }

const RARITY = ['', 'Everyday', 'Fine', 'Masterwork'];
const rarityTone = (r: number) => (r >= 3 ? 'gold' : r === 2 ? 'green' : 'grey');

/** Every piece of a hero's an upgrade fits, with the rank it would reach and its price (or why not). */
function targetsFor(items: readonly GearItem[], upgradeId: number) {
  const b = getBridge();
  if (!b) return [];
  const out: { it: GearItem; rank: number; price: number; why?: string }[] = [];
  for (const it of items) {
    try {
      const o = b.upgradeOptions(it.defId, it.upgrades).options.find((x) => x.id === upgradeId);
      if (o) out.push({ it, rank: o.rank, price: o.price, ...(o.why ? { why: o.why } : {}) });
    } catch { /* an unknown def */ }
  }
  return out.sort((a, b) => Number(b.it.slot !== null) - Number(a.it.slot !== null) || (a.it.id < b.it.id ? -1 : 1));
}

function OfferRow({ up, chosen, onPick }: { up: UpgradeSpec; chosen: boolean; onPick(): void }) {
  const w = describeUpgrade(up, 1);
  const style = up.look.glow ? ({ '--fk-glow': up.look.glow } as CSSProperties) : undefined;
  return (
    <li className={`fk-counter__row fk-ws__offer is-${up.kind}${chosen ? ' fk-counter__row--shown' : ''}`} style={style} data-testid="workshop-offer" data-upgrade={up.id} data-name={up.name}>
      <button type="button" className="fk-counter__look fk-ws__offer-look" onClick={onPick} aria-pressed={chosen}>
        <span className={`fk-ws__glyph is-${up.kind}`} aria-hidden>{up.kind === 'enchant' ? '✦' : '⚒'}</span>
        <span className="fk-talk__good-name">
          {up.name} <Badge tone={rarityTone(up.rarity)}>{RARITY[up.rarity]}</Badge>
          <span className="fk-counter__owner">{[...w.lines, ...(w.ability ? [`${w.ability.name}`] : [])].join(' · ')}{up.max_rank > 1 ? ` · ranks I–${roman(up.max_rank)}` : ''}</span>
        </span>
      </button>
      <span className="fk-ws__from">from {up.price}g</span>
    </li>
  );
}

export function Workshop({ worldId, workshop, heroes, busy, onApply, onClose }: {
  worldId: bigint; workshop: WorkshopView; heroes: WorkshopHero[]; busy?: string;
  onApply(itemId: bigint, upgradeId: number, payerId: bigint, price: number, label: string): void; onClose(): void;
}) {
  const title = SHOP_TITLES[workshop.shop];
  const kind = shopKind(workshop.shop);
  const offers = workshop.offers.map(upgradeSpec).filter((u): u is UpgradeSpec => !!u);
  const [upId, setUpId] = useState<number | undefined>(offers[0]?.id);
  const [heroId, setHeroId] = useState<bigint | undefined>(heroes[0]?.id);
  const [itemId, setItemId] = useState<bigint>();
  const up = offers.find((o) => o.id === upId);
  const g = useHeroGear(worldId, heroId);
  const targets = useMemo(() => (up ? targetsFor(g.items, up.id) : []), [g.items, up]);
  const chosen = targets.find((t) => t.it.id === itemId) ?? targets.find((t) => !t.why);
  const hero = heroes.find((h) => h.id === heroId);
  // what the hero's numbers would be with the next rank on a worn piece
  const after = useMemo(() => {
    if (!chosen || !up || chosen.it.slot === null) return undefined;
    const worn = g.items.filter((i) => i.slot !== null).map((i) => (i.id === chosen.it.id ? { ...i, upgrades: [...i.upgrades, up.id] } : i));
    return g.previewWorn(worn.flatMap((i) => [i.slot!, i.defId, i.tier]), upgradeSegs(worn));
  }, [chosen, up, g]);
  const verb = kind === 'enchant' ? 'Enchant' : 'Improve';
  const nextWords = up && chosen ? describeUpgrade(up, chosen.rank) : undefined;
  const short = !!chosen && !!hero && hero.gold < chosen.price;
  return (
    <Panel variant="solid" className="fk-talk__counter fk-counter fk-ws fk-hit" label={`${title}: upgrades`} data-testid="workshop" data-shop={workshop.shop}>
      <header className="fk-talk__counter-head">
        <span>{title}</span>
        <Button small variant="ghost" onClick={onClose} aria-label="Close the workshop" data-testid="workshop-close">✕</Button>
      </header>
      <p className="fk-ws__trade">{workshop.keeper ? <><b>{workshop.keeper}</b> — </> : null}{SHOP_TRADES[workshop.shop]}</p>
      {offers.length === 0 ? <p className="fk-faint fk-counter__empty">Nothing to offer today.</p> : (
        <ul className="fk-talk__goods fk-ws__offers" aria-label="On offer">
          {offers.map((o) => <OfferRow key={o.id} up={o} chosen={o.id === upId} onPick={() => { setUpId(o.id); setItemId(undefined); }} />)}
        </ul>
      )}
      {up && (
        <div className="fk-ws__apply" data-testid="workshop-apply" data-upgrade={up.id}>
          <div className="fk-ws__apply-head"><b>{up.name}</b><span className="fk-faint">{fitsLine(up)}.</span></div>
          <p className="fk-inv__lore">“{up.flavour}”</p>
          {heroes.length > 1 && (
            <Segmented label="Whose gear" className="fk-counter__tabs" value={heroId?.toString() ?? ''} onChange={(v) => { setHeroId(BigInt(v)); setItemId(undefined); }}
              options={heroes.map((h) => ({ value: h.id.toString(), label: h.name }))} />
          )}
          {targets.length === 0 ? <p className="fk-faint fk-counter__empty">{hero?.name ?? 'This hero'} carries nothing it fits.</p> : (
            <ul className="fk-talk__goods fk-ws__targets" aria-label="Pieces it fits">
              {targets.map((t) => {
                const now = ranksOf(t.it.upgrades).find((r) => r.up.id === up.id)?.rank ?? 0;
                const on = chosen?.it.id === t.it.id;
                return (
                  <li key={t.it.id.toString()} className={`fk-counter__row${on ? ' fk-counter__row--shown' : ''}`} data-testid="workshop-target" data-item-id={t.it.id.toString()} data-item={t.it.name}>
                    <button type="button" className="fk-counter__look" onClick={() => setItemId(t.it.id)} aria-pressed={on} disabled={!!t.why}>
                      <ItemIcon def={t.it.defId} tier={t.it.tier} name={t.it.name} relic={t.it.relic} scale={2} framed upgrades={t.it.upgrades} />
                      <span className="fk-talk__good-name">{upgradedName(t.it.name, t.it.upgrades)}
                        <span className="fk-counter__owner">{t.it.slot !== null ? 'worn' : 'in the pack'}{t.why ? ` · ${t.why}` : now ? ` · ${up.name} ${roman(now)} → ${roman(t.rank)}` : ''}</span>
                      </span>
                    </button>
                    {!t.why && <Badge tone="gold">{t.price}g</Badge>}
                  </li>
                );
              })}
            </ul>
          )}
          {chosen && !chosen.why && nextWords && (
            <div className="fk-counter__details fk-ws__preview" data-testid="workshop-preview">
              <span className="fk-counter__details-name">{upgradedName(chosen.it.name, [...chosen.it.upgrades, up.id])}</span>
              <span className="fk-counter__details-kind">{nextWords.name}: {[...nextWords.lines, ...(nextWords.ability ? [`${nextWords.ability.name} — ${nextWords.ability.effects.join('; ')} (${nextWords.ability.head})`] : [])].join(' · ')}</span>
              {after && g.stats && (
                <ul className="fk-ws__stats" data-testid="workshop-stats">
                  {STAT_ROWS.map((r) => {
                    const d = statDiff(g.stats!, after)[r.key];
                    if (!d) return null;
                    return <li key={r.key}><OryxIcon name={r.icon} scale={1} /> {r.label} {statValue(g.stats!, r.key)} → <b className={d > 0 ? 'is-up' : 'is-down'}>{statValue(after, r.key)}</b></li>;
                  })}
                </ul>
              )}
              <div className="fk-ws__pay">
                <span className="fk-counter__details-foot">{hero?.name} pays {chosen.price} gold{hero ? ` (${hero.gold} in purse)` : ''}. A haggler pays less.</span>
                <Button small primary disabled={!!busy || short || !heroId} loading={busy === `up-${chosen.it.id}-${up.id}`} title={short ? `${hero?.name} has ${hero?.gold} gold` : undefined}
                  onClick={() => heroId && onApply(chosen.it.id, up.id, heroId, chosen.price, `${chosen.it.name}: ${nextWords.name}`)} data-testid="workshop-buy">
                  {verb} · {chosen.price}g
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
